import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { TreeStructure, Factory, Trash, ArrowsLeftRight, Gear } from '@phosphor-icons/react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { useT } from '@/i18n/LanguageContext';
import { useAuth, canEditMfg } from '@/auth/AuthContext';
import { mfgApi } from './mfgApi';
import { DataTable } from '@/components/ui/DataTable';
import ItemPickerField from './ItemPickerField';
import { formatDate } from '@/utils/datetime';

export default function BomList() {
  const { t } = useT();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canWrite = canEditMfg(user);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [moveRow, setMoveRow] = useState(null);   // BOM row being re-parented
  const [newItemId, setNewItemId] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    mfgApi.boms().then((d) => setRows(Array.isArray(d) ? d : [])).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const fmtDate = (v) => {
    if (!v) return '—';
    try { return formatDate(v); } catch { return v; }
  };

  const del = async (r) => {
    if (!window.confirm(t('mfg.bomList.confirmDelete', { name: r.item_name || r.item_sku || '' }))) return;
    try {
      await mfgApi.deleteBom(r.item_id);
      toast.success(t('mfg.bomList.deleted'));
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || t('mfg.error')); }
  };

  const openMove = (r) => { setMoveRow(r); setNewItemId(''); };
  const doMove = async () => {
    if (!newItemId) { toast.error(t('mfg.bomList.pickNewItem')); return; }
    setBusy(true);
    try {
      await mfgApi.moveBom(moveRow.item_id, newItemId);
      toast.success(t('mfg.bomList.moved'));
      setMoveRow(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || t('mfg.error')); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-5 page-enter" data-testid="mfg-bom-list">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-zinc-900">{t('mfg.bomList.title')}</h1>
          <p className="text-sm text-zinc-500 mt-0.5">{t('mfg.bomList.desc')}</p>
        </div>
        {!loading && <span className="text-sm text-zinc-400" data-testid="bom-list-count">{t('mfg.bomList.count', { n: rows.length })}</span>}
      </div>

      <DataTable
        dataTestId="bom-list-table"
        rows={rows}
        rowKey={(r) => r.item_id}
        rowTestId={(r) => `bom-list-row-${r.item_id}`}
        onRowClick={(r) => navigate(`/mfg/bom?item=${r.item_id}`)}
        defaultSort={{ key: 'item_name', dir: 'asc' }}
        empty={{ icon: <TreeStructure size={40} weight="duotone" className="text-zinc-300 mx-auto mb-2" />, text: t('mfg.bomList.empty'), testid: 'bom-list-empty' }}
        columns={[
          {
            key: 'item_name', primary: true, header: t('mfg.bomList.item'),
            sortValue: (r) => r.item_name || '',
            cell: (r) => (
              <div className="flex flex-col">
                <span className="font-medium text-zinc-800">{r.item_name || '—'}</span>
                {r.item_sku && <span className="font-mono text-[11px] text-zinc-400">{r.item_sku}</span>}
              </div>
            ),
          },
          {
            key: 'component_count', header: t('mfg.bomList.components'), align: 'right',
            cell: (r) => <span className="tabular-nums text-zinc-700">{r.component_count} {t('mfg.bomList.componentsUnit')}</span>,
          },
          {
            key: 'work_center_name', header: t('mfg.bomList.station'),
            sortValue: (r) => r.work_center_name || '',
            cell: (r) => (r.work_center_name
              ? <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200 font-normal whitespace-nowrap"><Factory size={12} weight="duotone" className="mr-1" />{r.work_center_name}</Badge>
              : <span className="text-zinc-300">{t('mfg.bomList.stationNone')}</span>),
          },
          {
            key: 'equipment_names', header: t('mfg.bomList.equipment'),
            sortValue: (r) => (r.equipment_names || []).join(','),
            cell: (r) => ((r.equipment_names || []).length
              ? <div className="flex flex-wrap gap-1">{r.equipment_names.map((n, i) => (
                  <Badge key={`${n}-${i}`} variant="outline" className="bg-sky-50 text-sky-700 border-sky-200 font-normal whitespace-nowrap"><Gear size={12} weight="duotone" className="mr-1" />{n}</Badge>
                ))}</div>
              : <span className="text-zinc-300">—</span>),
          },
          {
            key: 'standard_hours', header: t('mfg.bomList.standardHours'), align: 'right',
            sortValue: (r) => r.standard_hours ?? -1,
            cell: (r) => (r.standard_hours != null
              ? <span className="tabular-nums text-indigo-600">{r.standard_hours} {t('mfg.bom.hoursUnit')}</span>
              : <span className="text-zinc-300">—</span>),
          },
          {
            key: 'updated_at', header: t('mfg.bomList.updated'), align: 'right',
            cell: (r) => <span className="text-xs text-zinc-500 tabular-nums">{fmtDate(r.updated_at)}</span>,
          },
          canWrite && {
            key: 'actions', header: '', actions: true,
            cell: (r) => (
              <>
                <Button size="sm" variant="ghost" data-testid={`bom-move-${r.item_id}`} onClick={() => openMove(r)}
                  className="h-8 px-2 text-zinc-500 hover:text-orange-600" title={t('mfg.bomList.replaceMain')}>
                  <ArrowsLeftRight size={16} />
                </Button>
                <Button size="sm" variant="ghost" data-testid={`bom-delete-${r.item_id}`} onClick={() => del(r)}
                  className="h-8 px-2 text-zinc-500 hover:text-rose-600" title={t('mfg.bomList.delete')}>
                  <Trash size={16} />
                </Button>
              </>
            ),
          },
        ]}
      />

      {/* 主部品を変更 (replace main part) */}
      <Dialog open={!!moveRow} onOpenChange={(o) => !o && setMoveRow(null)}>
        <DialogContent className="max-w-md" data-testid="bom-move-dialog">
          <DialogHeader>
            <DialogTitle>{t('mfg.bomList.replaceMain')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded-lg bg-zinc-50 border border-zinc-200 p-3">
              <p className="text-[11px] uppercase tracking-wider text-zinc-400 font-semibold">{t('mfg.bomList.currentMain')}</p>
              <p className="text-sm font-medium text-zinc-800 mt-0.5">{moveRow?.item_name || '—'}
                {moveRow?.item_sku && <span className="font-mono text-xs text-zinc-400 ml-2">{moveRow.item_sku}</span>}</p>
            </div>
            <div>
              <p className="text-xs font-semibold text-zinc-600 mb-1">{t('mfg.bomList.newMain')}</p>
              <ItemPickerField testid="bom-move-item" value={newItemId} excludeId={moveRow?.item_id}
                onChange={(id) => setNewItemId(id)} placeholderKey="mfg.bom.selectItem" />
            </div>
            <p className="text-[11px] text-zinc-400 leading-relaxed">{t('mfg.bomList.replaceHint')}</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMoveRow(null)} data-testid="bom-move-cancel">{t('mfg.cancel')}</Button>
            <Button onClick={doMove} disabled={busy || !newItemId} data-testid="bom-move-confirm" className="bg-teal-700 hover:bg-teal-800">
              {busy ? t('mfg.saving') : t('mfg.bomList.replaceConfirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
